'use client';

import React, { useState, useCallback, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';

// ─── Asset URLs ───────────────────────────────────────────────────────────────
const LOGO_URL = 'https://rifarito.s3.amazonaws.com/uploads/client/logo/2570/LGmiganga_logo_05.png';
const PAGOMOVIL_LOGO = '/assets/images/image-1785507951211.png';
const BINANCE_LOGO = 'https://rifarito.s3.amazonaws.com/uploads/bank/logo/283/ven-binance.png';
const RIFARITO_WATERMARK = 'https://rifarito.s3.amazonaws.com/assets/rifaritowatermark-c.png';

const COUNTRY_CODES = [
  { flag: '🇩🇴', code: '+1', label: '🇩🇴 +1' },
  { flag: '🇺🇸', code: '+1', label: '🇺🇸 +1' },
  { flag: '🇵🇷', code: '+1', label: '🇵🇷 +1' },
  { flag: '🇪🇸', code: '+34', label: '🇪🇸 +34' },
  { flag: '🇬🇹', code: '+502', label: '🇬🇹 +502' },
  { flag: '🇸🇻', code: '+503', label: '🇸🇻 +503' },
  { flag: '🇭🇳', code: '+504', label: '🇭🇳 +504' },
  { flag: '🇳🇮', code: '+505', label: '🇳🇮 +505' },
  { flag: '🇨🇷', code: '+506', label: '🇨🇷 +506' },
  { flag: '🇵🇦', code: '+507', label: '🇵🇦 +507' },
  { flag: '🇵🇪', code: '+51', label: '🇵🇪 +51' },
  { flag: '🇲🇽', code: '+52', label: '🇲🇽 +52' },
  { flag: '🇨🇺', code: '+53', label: '🇨🇺 +53' },
  { flag: '🇦🇷', code: '+54', label: '🇦🇷 +54' },
  { flag: '🇨🇱', code: '+56', label: '🇨🇱 +56' },
  { flag: '🇨🇴', code: '+57', label: '🇨🇴 +57' },
  { flag: '🇻🇪', code: '+58', label: '🇻🇪 +58' },
  { flag: '🇧🇴', code: '+591', label: '🇧🇴 +591' },
  { flag: '🇪🇨', code: '+593', label: '🇪🇨 +593' },
  { flag: '🇵🇾', code: '+595', label: '🇵🇾 +595' },
  { flag: '🇺🇾', code: '+598', label: '🇺🇾 +598' },
];

interface RaffleConfig {
  id: string;
  title: string;
  description: string;
  image_url: string;
  image_alt: string;
  price: number;
  draw_date: string;
  total_tickets: number;
  sold_tickets: number;
  href: string;
  is_active: boolean;
}

interface TicketData {
  runlot: string;
  buyerName: string;
  phone: string;
  countryCode: string;
  ticketNumber: string;
  createdAt: string;
  quantity: number;
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
      });
    }
  }, [text]);
  return (
    <button onClick={handleCopy} className={`rsp-copy-btn${copied ? ' copied' : ''}`} title="Copiar" aria-label="Copiar">
      {copied ? (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      )}
    </button>
  );
}

function TicketCard({ ticket, raffleTitle }: { ticket: TicketData; raffleTitle: string }) {
  const date = new Date(ticket.createdAt);
  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');

  return (
    <div className="rsp-ticket-card">
      <div className="rsp-ticket-card-header">
        <span className="rsp-ticket-card-header-icon">🕐</span>
        <div className="rsp-ticket-card-logo-wrap">
          <img src={LOGO_URL} alt="Rifas Camiganga" className="rsp-ticket-card-logo" />
          <span className="rsp-ticket-card-brand">RIFAS CAMIGANGA</span>
        </div>
        <span className="rsp-ticket-card-header-icon">🌐</span>
      </div>
      <div className="rsp-ticket-card-body">
        <p className="rsp-ticket-card-raffle-title">{raffleTitle}</p>
        <hr className="rsp-ticket-card-divider" />
        <p className="rsp-ticket-card-runlot">RUNLOT: {ticket.runlot}</p>
        <div className="rsp-ticket-card-status">
          <span>🔍</span> Verificación Pendiente
        </div>
        <div className="rsp-ticket-card-info-row">
          <span className="icon">👤</span>
          <span>{ticket.buyerName}</span>
        </div>
        <div className="rsp-ticket-card-info-row">
          <span className="icon">📱</span>
          <span>{ticket.countryCode} {ticket.phone}</span>
        </div>
        <div className="rsp-ticket-card-info-row">
          <span className="icon">📅</span>
          <span>{year}-{String(date.getMonth()+1).padStart(2,'0')}-{day} {hours}:{minutes}:{seconds}</span>
        </div>
      </div>
      <div className="rsp-ticket-card-footer">
        <span className="rsp-ticket-card-date">{day} {month} {year}</span>
        <span className="rsp-ticket-card-time">10:00 PM</span>
      </div>
      <div className="rsp-ticket-card-number-wrap">
        <div className="rsp-ticket-card-number">{ticket.ticketNumber}</div>
      </div>
      <div className="rsp-ticket-card-watermark">rifascamiganga.com</div>
    </div>
  );
}

export default function DynamicRafflePage() {
  const router = useRouter();
  const [scrolled, setScrolled] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [raffleConfig, setRaffleConfig] = useState<RaffleConfig | null>(null);
  const [loading, setLoading] = useState(true);

  // Purchase form state
  const [ticketCount, setTicketCount] = useState(1);
  const [selectedPayment, setSelectedPayment] = useState<'pagomovil' | 'binance'>('pagomovil');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState('+58');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [fileName, setFileName] = useState('');
  const [fileObject, setFileObject] = useState<File | null>(null);
  const [totalFormatted, setTotalFormatted] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [purchasedTicket, setPurchasedTicket] = useState<TicketData[]>([]);
  const [showPaymentWarning, setShowPaymentWarning] = useState(false);

  // Payment info state loaded from Supabase
  const [paymentInfo, setPaymentInfo] = useState({
    bank: 'Banco Venezolano de Crédito',
    phone: '04262126049',
    cedula: '27200898',
    bankCode: '0104',
    titular: 'Cristhian Camilo Ortiz',
  });

  // Verifier state
  const [verifierQuery, setVerifierQuery] = useState('');
  const [verifierResults, setVerifierResults] = useState<TicketData[]>([]);
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifierError, setVerifierError] = useState('');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load active raffle config from Supabase
  useEffect(() => {
    async function loadConfig() {
      try {
        const supabase = createClient();
        const { data } = await supabase
          .from('raffle_config')
          .select('*')
          .eq('is_active', true)
          .limit(1)
          .single();
        if (data) {
          setRaffleConfig(data);
          const price = data.price ?? 1699;
          const formatted = (ticketCount * price).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          setTotalFormatted('Bs.S ' + formatted);
          // Load payment info from Supabase
          if (data.payment_bank || data.payment_phone || data.payment_cedula) {
            setPaymentInfo({
              bank: data.payment_bank || 'Banco Venezolano de Crédito',
              phone: data.payment_phone || '04262126049',
              cedula: data.payment_cedula || '27200898',
              bankCode: data.payment_bank_code || '0104',
              titular: data.payment_titular || 'Cristhian Camilo Ortiz',
            });
          }
        }
      } catch {
        // fallback silently
      } finally {
        setLoading(false);
      }
    }
    loadConfig();
  }, []);

  useEffect(() => {
    if (raffleConfig) {
      const price = raffleConfig.price ?? 1699;
      const total = ticketCount * price;
      const formatted = total.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      setTotalFormatted('Bs.S ' + formatted);
    }
  }, [ticketCount, raffleConfig]);

  const decreaseTickets = () => { if (ticketCount > 1) setTicketCount(ticketCount - 1); };
  const increaseTickets = () => setTicketCount(ticketCount + 1);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFileName(e.target.files[0].name);
      setFileObject(e.target.files[0]);
    }
  };

  const handleConfirm = async () => {
    if (!fullName.trim()) { setSubmitError('Por favor ingresa tu nombre completo.'); return; }
    if (!phone.trim()) { setSubmitError('Por favor ingresa tu número de teléfono.'); return; }
    if (!termsAccepted) { setSubmitError('Debes aceptar los Términos y Condiciones.'); return; }
    // Show warning modal before proceeding
    setShowPaymentWarning(true);
  };

  const handleConfirmAfterWarning = async () => {
    setShowPaymentWarning(false);
    setIsSubmitting(true);
    setSubmitError('');

    try {
      const supabase = createClient();
      let proofFileUrl: string | null = null;
      let proofFileName: string | null = null;

      if (fileObject) {
        const fileExt = fileObject.name.split('.').pop();
        const filePath = `proofs/${Date.now()}_${Math.random().toString(36).slice(2)}.${fileExt}`;
        const { error: uploadError } = await supabase.storage
          .from('payment-proofs')
          .upload(filePath, fileObject, { upsert: false });

        proofFileName = fileObject.name;
        if (!uploadError) {
          const { data: urlData } = supabase.storage.from('payment-proofs').getPublicUrl(filePath);
          proofFileUrl = urlData?.publicUrl ?? null;
        } else {
          setIsSubmitting(false);
          setSubmitError('Error al subir el comprobante. Por favor intenta de nuevo.');
          return;
        }
      }

      const now = new Date();
      const price = raffleConfig?.price ?? 1699;
      const raffleName = raffleConfig?.title ?? 'RIFA ACTIVA';

      const { data: insertedData, error } = await supabase.from('ticket_orders').insert({
        raffle_name: raffleName,
        buyer_name: fullName.trim(),
        phone: phone.trim(),
        country_code: countryCode,
        ticket_quantity: ticketCount,
        ticket_price: price,
        total_amount: ticketCount * price,
        payment_method: selectedPayment === 'pagomovil' ? 'Pago Móvil - Banco Venezolano de Crédito' : 'Binance (No disponible)',
        proof_file_name: proofFileName,
        proof_file_url: proofFileUrl,
        status: 'pending',
      }).select().single();

      if (error) {
        setSubmitError('Hubo un error al registrar tu pedido. Por favor intenta de nuevo.');
        return;
      }

      const baseTicketNum = insertedData?.id
        ? parseInt(insertedData.id.replace(/-/g, '').slice(0, 8), 16) % 9000 + 1000
        : Math.floor(Math.random() * 9000) + 1000;

      const tickets: TicketData[] = Array.from({ length: ticketCount }, (_, i) => ({
        runlot: `CNL-ORF-${now.getFullYear()}-${String(Math.floor(Math.random() * 999999)).padStart(6, '0')}`,
        buyerName: fullName.trim(),
        phone: phone.trim(),
        countryCode,
        ticketNumber: String(baseTicketNum + i),
        createdAt: insertedData?.created_at ?? now.toISOString(),
        quantity: ticketCount,
      }));

      setPurchasedTicket(tickets);

      setSubmitSuccess(true);
      setFullName(''); setPhone(''); setTicketCount(1);
      setFileName(''); setFileObject(null); setTermsAccepted(false);
    } catch {
      setSubmitError('Hubo un error inesperado. Por favor intenta de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerify = async () => {
    if (!verifierQuery.trim()) { setVerifierError('Por favor ingresa tu número de teléfono.'); return; }
    setIsVerifying(true);
    setVerifierError('');
    setVerifierResults([]);
    try {
      const supabase = createClient();
      const { data, error } = await supabase
        .from('ticket_orders')
        .select('*')
        .ilike('phone', `%${verifierQuery.trim()}%`)
        .order('created_at', { ascending: false });

      if (error) { setVerifierError('Error al buscar. Por favor intenta de nuevo.'); return; }
      if (!data || data.length === 0) { setVerifierError('No se encontraron tickets con ese número de teléfono.'); return; }

      const results = data.map((row: any) => {
        const createdDate = new Date(row.created_at);
        const runlot = `CNL-ORF-${createdDate.getFullYear()}-${String(parseInt(row.id.replace(/-/g, '').slice(0, 8), 16) % 999999).padStart(6, '0')}`;
        const ticketNum = String(parseInt(row.id.replace(/-/g, '').slice(0, 8), 16) % 9000 + 1000);
        return {
          runlot,
          buyerName: row.buyer_name,
          phone: row.phone,
          countryCode: row.country_code,
          ticketNumber: ticketNum,
          createdAt: row.created_at,
          quantity: row.ticket_quantity,
        };
      });
      setVerifierResults(results);
    } catch {
      setVerifierError('Error inesperado. Por favor intenta de nuevo.');
    } finally {
      setIsVerifying(false);
    }
  };

  const raffleTitle = raffleConfig?.title ?? '';
  const raffleImage = raffleConfig?.image_url ?? '';
  const raffleImageAlt = raffleConfig?.image_alt ?? 'Imagen de la rifa';
  const raffleDate = raffleConfig?.draw_date ?? '';
  const raffleDescription = raffleConfig?.description ?? '';
  const rafflePrice = raffleConfig?.price ?? 1699;
  const raffleSold = raffleConfig?.sold_tickets ?? 0;
  const raffleTotal = raffleConfig?.total_tickets ?? 10000;
  const progressPct = raffleTotal > 0 ? parseFloat(((raffleSold / raffleTotal) * 100).toFixed(1)) : 0;

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#3d3c25',
        fontFamily: 'Poppins, sans-serif',
        color: '#fff',
        fontSize: '18px',
        fontWeight: 700,
      }}>
        Cargando rifa...
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800;900&display=swap');
        * { box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Poppins', sans-serif; margin: 0; padding: 0; background-color: #FBFBFB; }

        .rsp-navbar { background-color: #fff; border-top: 8px solid #948f30; border-bottom: 8px solid #948f30; position: fixed; top: 0; left: 0; width: 100%; z-index: 1000; transition: all 0.2s linear; }
        .rsp-navbar.scrolled { box-shadow: 0 5px 6px 0 rgba(148,143,48,0.42); }
        .rsp-navbar-container { display: flex; align-items: center; justify-content: space-between; max-width: 1140px; margin: 0 auto; padding: 0 15px; height: 75px; }
        .rsp-nav-links { display: flex; align-items: center; gap: 30px; list-style: none; margin: 0; padding: 0; }
        .rsp-nav-links li a { font-family: 'Poppins', sans-serif; font-weight: 900; font-size: 14px; color: #000; text-decoration: none; transition: color 0.2s; display: flex; flex-direction: column; align-items: center; line-height: 1.2; }
        .rsp-nav-links li a .nav-icon { font-size: 16px; }
        .rsp-nav-links li a .nav-sub { font-size: 11px; font-weight: 600; color: #666; }
        .rsp-nav-links li a:hover { color: #fa5012; }
        .rsp-btn-comprar { background-color: #948f30; font-family: 'Poppins', sans-serif; font-size: 15px; font-weight: 800; text-transform: uppercase; color: #fff !important; padding: 10px 28px; border-radius: 100px; text-decoration: none; transition: background-color 0.2s, box-shadow 0.2s; box-shadow: 0 0 18px rgba(148,143,48,0.29); display: inline-block; white-space: nowrap; }
        .rsp-btn-comprar:hover { background-color: #fa5012; box-shadow: 0 0 18px rgba(250,80,18,0.54); color: #fff !important; }

        .rsp-hero { background: #3d3c25; padding: 110px 0 40px; min-height: 520px; }
        .rsp-hero-container { max-width: 1140px; margin: 0 auto; padding: 0 15px; display: flex; align-items: flex-start; gap: 40px; }
        .rsp-hero-img { flex: 0 0 48%; max-width: 48%; }
        .rsp-hero-img img { width: 100%; height: auto; border-radius: 18px; box-shadow: 0 0 18px rgba(148,143,48,0.41); }
        .rsp-hero-info { flex: 1; color: #fff; }
        .rsp-hero-meta { display: flex; align-items: center; gap: 18px; margin-bottom: 10px; flex-wrap: wrap; }
        .rsp-hero-meta-item { display: flex; align-items: center; gap: 6px; font-family: 'Poppins', sans-serif; font-size: 16px; font-weight: 700; color: #fff; background: rgba(0,0,0,0.25); border-radius: 8px; padding: 4px 12px; }
        .rsp-hero-progress-wrap { margin: 10px 0 14px; position: relative; height: 26px; width: 100%; }
        .rsp-hero-progress-bg { position: absolute; top: 0; left: 0; width: 100%; height: 20px; border-radius: 10px; background: rgba(148,143,48,0.15); border: 2px solid rgba(148,143,48,0.5); }
        .rsp-hero-progress-fill { position: absolute; top: 0; left: 0; height: 20px; background: linear-gradient(to right, rgba(148,143,48,0.5), #948f30); border-radius: 10px; border-right: 2px solid #fff; }
        .rsp-hero-progress-label { position: absolute; top: 2px; transform: translateX(-100%); font-size: 11px; font-weight: 700; font-family: 'Poppins', sans-serif; color: #fff; white-space: nowrap; background: #948f30; border-radius: 4px; padding: 0 5px; }
        .rsp-hero-title { font-family: 'Poppins', sans-serif; font-weight: 900; font-size: 42px; line-height: 1.1; color: #fff; margin: 0 0 16px; text-transform: uppercase; }
        .rsp-hero-desc { font-family: 'Poppins', sans-serif; font-size: 15px; font-weight: 500; color: #f0eecc; line-height: 1.7; margin: 0; white-space: pre-line; }

        .rsp-purchase-section { background: #f5f5f5; padding: 40px 0 20px; }
        .rsp-purchase-container { max-width: 720px; margin: 0 auto; padding: 0 15px; }
        .rsp-purchase-card { background: #fff; border-radius: 18px; box-shadow: 0 2px 24px rgba(0,0,0,0.08); padding: 32px 36px 28px; position: relative; overflow: hidden; }
        .rsp-tickets-header { text-align: center; margin-bottom: 20px; }
        .rsp-tickets-title { font-family: 'Poppins', sans-serif; font-weight: 900; font-size: 28px; color: #948f30; letter-spacing: 3px; margin: 0 0 14px; text-transform: uppercase; }
        .rsp-ticket-counter { display: flex; align-items: center; justify-content: center; gap: 0; margin-bottom: 8px; }
        .rsp-counter-btn { width: 34px; height: 34px; border-radius: 50%; border: 2px solid #ccc; background: #f5f5f5; font-size: 20px; font-weight: 700; color: #555; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all 0.2s; line-height: 1; }
        .rsp-counter-btn:hover:not(:disabled) { border-color: #948f30; color: #948f30; background: #f9f8e8; }
        .rsp-counter-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .rsp-counter-display { display: flex; flex-direction: column; align-items: center; justify-content: center; min-width: 60px; margin: 0 8px; }
        .rsp-counter-num { font-family: 'Poppins', sans-serif; font-size: 22px; font-weight: 800; color: #1f2224; line-height: 1; }
        .rsp-counter-label { font-family: 'Poppins', sans-serif; font-size: 10px; font-weight: 700; color: #888; letter-spacing: 1px; text-transform: uppercase; }
        .rsp-total-price { font-family: 'Poppins', sans-serif; font-size: 22px; font-weight: 800; color: #1f2224; text-align: center; margin: 6px 0 0; }
        .rsp-total-price span { color: #948f30; }

        .rsp-section-title { font-family: 'Poppins', sans-serif; font-size: 17px; font-weight: 800; color: #1f2224; text-transform: uppercase; margin: 22px 0 12px; display: flex; align-items: center; gap: 8px; }
        .rsp-section-title .icon { font-size: 18px; }
        .rsp-form-row { display: flex; gap: 14px; margin-bottom: 14px; }
        .rsp-form-group { flex: 1; display: flex; flex-direction: column; }
        .rsp-form-label { font-family: 'Poppins', sans-serif; font-size: 13px; font-weight: 600; color: #555; margin-bottom: 5px; }
        .rsp-form-input { font-family: 'Poppins', sans-serif; font-size: 14px; color: #333; border: 1px solid #ced4da; border-radius: 6px; padding: 8px 12px; outline: none; transition: border-color 0.2s, box-shadow 0.2s; width: 100%; }
        .rsp-form-input:focus { border-color: #948f30; box-shadow: 0 0 0 3px rgba(148,143,48,0.15); }
        .rsp-phone-row { display: flex; gap: 8px; }
        .rsp-phone-select { font-family: 'Poppins', sans-serif; font-size: 14px; color: #333; border: 1px solid #ced4da; border-radius: 6px; padding: 8px 6px; outline: none; background: #fff; cursor: pointer; min-width: 100px; transition: border-color 0.2s; }
        .rsp-phone-select:focus { border-color: #948f30; box-shadow: 0 0 0 3px rgba(148,143,48,0.15); }

        .rsp-payment-subtitle { font-family: 'Poppins', sans-serif; font-size: 13px; font-weight: 500; color: #888; margin: 0 0 12px; }
        .rsp-payment-options { display: flex; gap: 12px; margin-bottom: 16px; }
        .rsp-payment-option { position: relative; cursor: pointer; border-radius: 12px; border: 2px solid #e0e0e0; padding: 6px; transition: border-color 0.2s; display: flex; align-items: center; justify-content: center; width: 72px; height: 72px; }
        .rsp-payment-option.active { border-color: #948f30; }
        .rsp-payment-option img { width: 52px; height: 52px; object-fit: contain; border-radius: 8px; }
        .rsp-payment-check { position: absolute; bottom: -6px; right: -6px; width: 20px; height: 20px; background: #948f30; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 11px; font-weight: 700; }
        .rsp-payment-details { background: #f9f9f9; border-radius: 12px; padding: 16px 20px; margin-bottom: 14px; }
        .rsp-payment-name { font-family: 'Poppins', sans-serif; font-size: 15px; font-weight: 800; color: #1f2224; margin: 0 0 10px; display: flex; align-items: center; gap: 6px; }
        .rsp-payment-name .badge { font-size: 10px; background: #948f30; color: #fff; border-radius: 4px; padding: 1px 6px; font-weight: 700; }
        .rsp-payment-field-label { font-family: 'Poppins', sans-serif; font-size: 12px; font-weight: 700; color: #888; text-transform: uppercase; letter-spacing: 0.5px; margin: 8px 0 2px; }
        .rsp-payment-field-value { display: flex; align-items: center; gap: 8px; }
        .rsp-payment-field-value h3 { font-family: 'Poppins', sans-serif; font-size: 20px; font-weight: 800; color: #1f2224; margin: 0; }
        .rsp-payment-titular { font-family: 'Poppins', sans-serif; font-size: 13px; color: #555; margin: 10px 0 0; }
        .rsp-payment-total-badge { display: inline-block; background: #1f2224; color: #fff; border-radius: 20px; padding: 5px 16px; font-family: 'Poppins', sans-serif; font-size: 14px; font-weight: 700; margin-top: 12px; }
        .rsp-payment-total-badge span { font-weight: 400; font-size: 13px; opacity: 0.8; }

        .rsp-copy-btn { background: #948f30; border: none; border-radius: 50%; width: 28px; height: 28px; display: inline-flex; align-items: center; justify-content: center; cursor: pointer; color: #fff; font-size: 13px; transition: background 0.2s; flex-shrink: 0; }
        .rsp-copy-btn:hover { background: #fa5012; }
        .rsp-copy-btn.copied { background: #28a745; }

        .rsp-upload-area { border: 2px dashed #ccc; border-radius: 10px; padding: 18px 16px; display: flex; align-items: center; gap: 12px; cursor: pointer; transition: border-color 0.2s; position: relative; margin-bottom: 8px; }
        .rsp-upload-area:hover { border-color: #948f30; }
        .rsp-upload-icon { width: 36px; height: 36px; background: #f0eecc; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #948f30; }
        .rsp-upload-text { font-family: 'Poppins', sans-serif; font-size: 13px; color: #888; flex: 1; }
        .rsp-upload-btn { background: #948f30; color: #fff; border: none; border-radius: 6px; padding: 6px 14px; font-family: 'Poppins', sans-serif; font-size: 13px; font-weight: 700; cursor: pointer; transition: background 0.2s; white-space: nowrap; }
        .rsp-upload-btn:hover { background: #fa5012; }
        .rsp-upload-input { position: absolute; inset: 0; opacity: 0; cursor: pointer; width: 100%; height: 100%; }
        .rsp-upload-summary { font-family: 'Poppins', sans-serif; font-size: 13px; font-weight: 700; color: #555; margin: 4px 0 0; }

        .rsp-terms-row { display: flex; align-items: center; gap: 10px; margin: 18px 0 14px; }
        .rsp-terms-row input[type="checkbox"] { width: 16px; height: 16px; accent-color: #948f30; cursor: pointer; flex-shrink: 0; }
        .rsp-terms-label { font-family: 'Poppins', sans-serif; font-size: 14px; color: #333; }
        .rsp-terms-label a { color: #948f30; font-weight: 700; text-decoration: none; }
        .rsp-terms-label a:hover { text-decoration: underline; }

        .rsp-confirm-btn { width: 100%; background: #948f30; color: #fff; border: none; border-radius: 100px; padding: 14px 0; font-family: 'Poppins', sans-serif; font-size: 16px; font-weight: 800; text-transform: uppercase; letter-spacing: 1px; cursor: pointer; transition: background 0.2s, box-shadow 0.2s; box-shadow: 0 0 18px rgba(148,143,48,0.29); }
        .rsp-confirm-btn:hover { background: #fa5012; box-shadow: 0 0 18px rgba(250,80,18,0.54); }
        .rsp-recaptcha-note { text-align: center; margin-top: 14px; font-family: 'Poppins', sans-serif; font-size: 12px; color: #888; }
        .rsp-recaptcha-note a { color: #948f30; text-decoration: none; }
        .rsp-help-center { text-align: center; margin-top: 6px; font-family: 'Poppins', sans-serif; font-size: 13px; color: #555; }
        .rsp-help-center strong { color: #948f30; }

        .rsp-verifier-section { background: #fff; padding: 50px 0; }
        .rsp-verifier-container { max-width: 600px; margin: 0 auto; padding: 0 15px; text-align: center; }
        .rsp-verifier-title { font-family: 'Poppins', sans-serif; font-weight: 900; font-size: 38px; color: #948f30; text-transform: uppercase; line-height: 1.1; margin: 0 0 4px; }
        .rsp-verifier-subtitle { font-family: 'Poppins', sans-serif; font-size: 16px; font-weight: 600; color: #555; margin: 0 0 24px; text-transform: uppercase; letter-spacing: 2px; }
        .rsp-verifier-input-wrap { position: relative; margin-bottom: 16px; }
        .rsp-verifier-input { width: 100%; border: 1px solid #ced4da; border-radius: 8px; padding: 12px 16px; font-family: 'Poppins', sans-serif; font-size: 14px; color: #333; outline: none; transition: border-color 0.2s; }
        .rsp-verifier-input:focus { border-color: #948f30; box-shadow: 0 0 0 3px rgba(148,143,48,0.15); }
        .rsp-verifier-btn { background: #948f30; color: #fff; border: none; border-radius: 100px; padding: 12px 36px; font-family: 'Poppins', sans-serif; font-size: 15px; font-weight: 800; cursor: pointer; transition: background 0.2s, box-shadow 0.2s; box-shadow: 0 0 18px rgba(148,143,48,0.29); display: inline-flex; align-items: center; gap: 8px; }
        .rsp-verifier-btn:hover { background: #fa5012; box-shadow: 0 0 18px rgba(250,80,18,0.54); }

        .rsp-footer { background-color: #948f30; color: #fff; padding: 55px 0 30px; }
        .rsp-footer-container { max-width: 1140px; margin: 0 auto; padding: 0 15px; display: flex; flex-wrap: wrap; gap: 0; }
        .rsp-footer-col { flex: 0 0 25%; max-width: 25%; padding: 0 15px; margin-bottom: 24px; }
        .rsp-footer-col-title { font-family: 'Poppins', sans-serif; font-weight: 800; font-size: 17px; color: #fff; text-transform: uppercase; margin: 0 0 14px; }
        .rsp-footer-brand-name { font-family: 'Poppins', sans-serif; font-weight: 900; font-size: 20px; color: #fff; margin: 0 0 6px; }
        .rsp-footer-brand-sub { font-family: 'Poppins', sans-serif; font-size: 13px; color: rgba(255,255,255,0.8); margin: 0; }
        .rsp-footer-links { list-style: none; padding: 0; margin: 0; }
        .rsp-footer-links li { margin-bottom: 8px; }
        .rsp-footer-links li a { font-family: 'Poppins', sans-serif; font-size: 14px; color: rgba(255,255,255,0.85); text-decoration: none; transition: color 0.2s; }
        .rsp-footer-links li a:hover { color: #fff; text-decoration: underline; }
        .rsp-footer-contact-list { list-style: none; padding: 0; margin: 0; }
        .rsp-footer-contact-list li { font-family: 'Poppins', sans-serif; font-size: 14px; color: rgba(255,255,255,0.85); margin-bottom: 8px; display: flex; align-items: flex-start; gap: 8px; }
        .rsp-footer-contact-list li a { color: rgba(255,255,255,0.85); text-decoration: none; }
        .rsp-footer-contact-list li a:hover { color: #fff; }
        .rsp-footer-social { display: flex; gap: 10px; margin-top: 4px; }
        .rsp-footer-social a { display: inline-flex; align-items: center; justify-content: center; width: 38px; height: 38px; border-radius: 50%; background: rgba(255,255,255,0.15); color: #fff; font-size: 18px; text-decoration: none; transition: background 0.2s; }
        .rsp-footer-social a:hover { background: #fa5012; }
        .rsp-footer-credits { max-width: 1140px; margin: 20px auto 0; padding: 20px 15px 0; border-top: 1px solid rgba(255,255,255,0.2); display: flex; flex-direction: column; align-items: center; gap: 8px; text-align: center; }
        .rsp-footer-credits p { font-family: 'Poppins', sans-serif; font-size: 12px; color: rgba(255,255,255,0.75); margin: 0; }
        .rsp-footer-credits a { color: rgba(255,255,255,0.7); text-decoration: none; }
        .rsp-footer-credits a:hover { color: #fff; }

        .rsp-wa-float-btn { display: flex; flex-direction: column; align-items: center; justify-content: center; position: fixed; bottom: 75px; right: 10px; width: 56px; height: 56px; border-radius: 50%; z-index: 9999; background: #25D366; color: #fff; text-decoration: none; box-shadow: 0 4px 16px rgba(37,211,102,0.55); transition: background 0.2s, transform 0.2s; user-select: none; }
        .rsp-wa-float-btn:hover { background: #1ebe5d; transform: scale(1.08); }
        .rsp-wa-float-btn-label { font-size: 9px; font-weight: 800; font-family: 'Poppins', sans-serif; color: #fff; line-height: 1; margin-top: 2px; white-space: nowrap; }
        .rsp-chat-btn { display: flex; flex-direction: column; align-items: center; justify-content: center; cursor: pointer; width: 50px; height: 50px; position: fixed; bottom: 15px; right: 10px; border-radius: 50%; z-index: 3; background: #948f30; color: #fff; transition: background 0.2s; user-select: none; }
        .rsp-chat-btn:hover { background: #fa5012; }
        .rsp-chat-btn-icon { font-size: 20px; line-height: 1; }
        .rsp-chat-btn-label { font-size: 10px; font-weight: 700; font-family: 'Poppins', sans-serif; color: #f0eecc; line-height: 1; }
        .rsp-chat-links { display: none; position: fixed; bottom: 70px; right: 10px; z-index: 2; flex-direction: column; gap: 6px; }
        .rsp-chat-links.active { display: flex; }
        .rsp-chat-links a { display: flex; align-items: center; justify-content: center; width: 50px; height: 50px; border-radius: 50%; color: #fff; font-size: 26px; text-decoration: none; transition: opacity 0.2s; }
        .rsp-chat-links a:hover { opacity: 0.85; }
        .rsp-chat-links a.whatsapp { background: #4dc247; }
        .rsp-chat-links a.telegram { background: #2196F3; }

        .rsp-ticket-card { background: linear-gradient(160deg, #3d3c25 0%, #5a5820 40%, #3d3c25 100%); border-radius: 18px; padding: 0; max-width: 320px; margin: 0 auto; box-shadow: 0 8px 32px rgba(0,0,0,0.35); overflow: hidden; font-family: 'Poppins', sans-serif; border: 2px solid rgba(148,143,48,0.5); position: relative; }
        .rsp-ticket-card-header { background: rgba(0,0,0,0.25); padding: 14px 18px 10px; display: flex; align-items: center; justify-content: space-between; border-bottom: 1px solid rgba(255,255,255,0.1); }
        .rsp-ticket-card-header-icon { font-size: 18px; color: rgba(255,255,255,0.7); }
        .rsp-ticket-card-logo-wrap { display: flex; flex-direction: column; align-items: center; gap: 2px; }
        .rsp-ticket-card-logo { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; border: 2px solid rgba(255,255,255,0.3); }
        .rsp-ticket-card-brand { font-size: 11px; font-weight: 800; color: #fff; letter-spacing: 1px; text-transform: uppercase; }
        .rsp-ticket-card-body { padding: 14px 18px 10px; }
        .rsp-ticket-card-raffle-title { font-size: 14px; font-weight: 900; color: #f0eecc; text-align: center; text-transform: uppercase; margin: 0 0 12px; letter-spacing: 0.5px; }
        .rsp-ticket-card-divider { border: none; border-top: 1px solid rgba(255,255,255,0.15); margin: 10px 0; }
        .rsp-ticket-card-runlot { font-size: 12px; font-weight: 700; color: #f0eecc; margin: 0 0 4px; letter-spacing: 0.5px; }
        .rsp-ticket-card-status { display: inline-flex; align-items: center; gap: 5px; background: rgba(255,165,0,0.2); border: 1px solid rgba(255,165,0,0.5); border-radius: 20px; padding: 2px 10px; font-size: 11px; font-weight: 700; color: #ffd700; margin-bottom: 10px; }
        .rsp-ticket-card-info-row { display: flex; align-items: center; gap: 7px; margin-bottom: 6px; font-size: 13px; color: #fff; font-weight: 600; }
        .rsp-ticket-card-info-row .icon { font-size: 14px; flex-shrink: 0; }
        .rsp-ticket-card-footer { background: rgba(0,0,0,0.3); padding: 10px 18px; display: flex; align-items: center; justify-content: space-between; border-top: 1px solid rgba(255,255,255,0.1); }
        .rsp-ticket-card-date { font-size: 12px; font-weight: 700; color: #f0eecc; text-transform: uppercase; }
        .rsp-ticket-card-time { font-size: 12px; font-weight: 700; color: #f0eecc; }
        .rsp-ticket-card-number-wrap { background: rgba(0,0,0,0.4); border-top: 2px dashed rgba(255,255,255,0.2); padding: 14px 18px; text-align: center; }
        .rsp-ticket-card-number { font-size: 36px; font-weight: 900; color: #fff; letter-spacing: 6px; line-height: 1; }
        .rsp-ticket-card-watermark { text-align: center; padding: 6px 0 10px; font-size: 11px; color: rgba(255,255,255,0.4); font-weight: 600; letter-spacing: 1px; }

        .rsp-warning-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.65); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 16px; }
        .rsp-warning-modal { background: #fff; border-radius: 18px; max-width: 480px; width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.4); overflow: hidden; font-family: 'Poppins', sans-serif; }
        .rsp-warning-modal-header { background: #7c2d12; padding: 18px 24px; display: flex; align-items: center; gap: 12px; }
        .rsp-warning-modal-header-icon { font-size: 28px; }
        .rsp-warning-modal-header-title { font-family: 'Poppins', sans-serif; font-size: 18px; font-weight: 800; color: #fff; margin: 0; }
        .rsp-warning-modal-body { padding: 20px 24px; }
        .rsp-warning-modal-body p { font-family: 'Poppins', sans-serif; font-size: 14px; color: #333; margin: 0 0 14px; line-height: 1.6; }
        .rsp-warning-modal-data { background: #f0fdf4; border: 2px solid #16a34a; border-radius: 12px; padding: 14px 18px; margin-bottom: 14px; }
        .rsp-warning-modal-data-title { font-family: 'Poppins', sans-serif; font-size: 14px; font-weight: 800; color: #15803d; margin: 0 0 10px; }
        .rsp-warning-modal-data-row { display: flex; align-items: center; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid rgba(22,163,74,0.15); font-family: 'Poppins', sans-serif; font-size: 14px; color: #1f2224; }
        .rsp-warning-modal-data-row:last-child { border-bottom: none; }
        .rsp-warning-modal-data-row .label { font-weight: 700; color: #555; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; }
        .rsp-warning-modal-data-row span:last-child { font-weight: 800; color: #1f2224; }
        .rsp-warning-modal-alert { background: #fff7ed; border: 1px solid #f97316; border-radius: 10px; padding: 12px 16px; font-family: 'Poppins', sans-serif; font-size: 13px; color: #9a3412; line-height: 1.6; }
        .rsp-warning-modal-footer { padding: 16px 24px 20px; display: flex; flex-direction: column; gap: 10px; }
        .rsp-warning-modal-confirm-btn { width: 100%; background: #948f30; color: #fff; border: none; border-radius: 100px; padding: 14px 0; font-family: 'Poppins', sans-serif; font-size: 15px; font-weight: 800; cursor: pointer; transition: background 0.2s, box-shadow 0.2s; box-shadow: 0 0 18px rgba(148,143,48,0.35); letter-spacing: 0.5px; }
        .rsp-warning-modal-confirm-btn:hover { background: #7a7628; box-shadow: 0 0 22px rgba(148,143,48,0.5); }
        .rsp-warning-modal-cancel-btn { width: 100%; background: transparent; color: #888; border: 1px solid #ddd; border-radius: 100px; padding: 11px 0; font-family: 'Poppins', sans-serif; font-size: 14px; font-weight: 600; cursor: pointer; transition: background 0.2s, color 0.2s; }
        .rsp-warning-modal-cancel-btn:hover { background: #f5f5f5; color: #555; }

        .rsp-success-wrap { text-align: center; }
        .rsp-success-badge { display: inline-flex; align-items: center; gap: 8px; background: #2e7d32; color: #fff; border-radius: 100px; padding: 10px 24px; font-family: 'Poppins', sans-serif; font-size: 16px; font-weight: 800; margin-bottom: 20px; box-shadow: 0 4px 16px rgba(46,125,50,0.4); }
        .rsp-whatsapp-channel-btn { display: inline-flex; align-items: center; gap: 10px; background: #25D366; color: #fff; border-radius: 100px; padding: 14px 24px; font-family: 'Poppins', sans-serif; font-size: 14px; font-weight: 800; text-decoration: none; margin-top: 18px; box-shadow: 0 4px 18px rgba(37,211,102,0.45); transition: background 0.2s, box-shadow 0.2s; line-height: 1.3; text-align: left; }
        .rsp-whatsapp-channel-btn:hover { background: #1ebe5d; box-shadow: 0 4px 22px rgba(37,211,102,0.6); }
        .rsp-whatsapp-channel-btn svg { flex-shrink: 0; }

        @media (max-width: 991px) {
          .rsp-hero { padding-top: 100px; }
          .rsp-hero-container { flex-direction: column; gap: 24px; }
          .rsp-hero-img { flex: 0 0 100%; max-width: 100%; }
          .rsp-hero-title { font-size: 32px; }
          .rsp-footer-col { flex: 0 0 50%; max-width: 50%; }
        }
        @media (max-width: 767px) {
          .rsp-hero-title { font-size: 26px; }
          .rsp-purchase-card { padding: 20px 16px; }
          .rsp-form-row { flex-direction: column; }
          .rsp-footer-col { flex: 0 0 100%; max-width: 100%; }
          .rsp-nav-links { gap: 12px; }
          .rsp-nav-links li a { font-size: 12px; }
        }
      `}</style>

      {/* ── NAVBAR ── */}
      <nav className={`rsp-navbar${scrolled ? ' scrolled' : ''}`}>
        <div className="rsp-navbar-container">
          <div style={{ flexShrink: 0 }}>
            <Link href="/">
              <img src={LOGO_URL} alt="Logo" style={{ width: '90px', height: 'auto', cursor: 'pointer' }} />
            </Link>
          </div>
          <ul className="rsp-nav-links">
            <li>
              <a href="#top">
                <span className="nav-icon">🏠</span>
                <span style={{ fontWeight: 900, fontSize: '13px' }}>INICIO</span>
                <span className="nav-sub">Home</span>
              </a>
            </li>
            <li>
              <a href="#tickets">
                <span className="nav-icon">🔍</span>
                <span style={{ fontWeight: 900, fontSize: '13px' }}>VERIFICADOR</span>
                <span className="nav-sub">Buscar tus números</span>
              </a>
            </li>
            <li>
              <a href="#contacto">
                <span className="nav-icon">💬</span>
                <span style={{ fontWeight: 900, fontSize: '13px' }}>CONTACTO</span>
                <span className="nav-sub">WhatsApp Ayuda</span>
              </a>
            </li>
          </ul>
          <a href="#comprar" className="rsp-btn-comprar">COMPRAR TICKETS</a>
        </div>
      </nav>

      <div id="top" />

      {/* ── HERO ── */}
      <section className="rsp-hero">
        <div className="rsp-hero-container">
          <div className="rsp-hero-img">
            {raffleImage && (
              <img src={raffleImage} alt={raffleImageAlt} />
            )}
          </div>
          <div className="rsp-hero-info">
            <div className="rsp-hero-meta">
              <div className="rsp-hero-meta-item">
                <span>📅</span>
                <strong>{raffleDate}</strong>
              </div>
              <div className="rsp-hero-meta-item">
                <span>🕙</span>
                <strong>10:00 PM</strong>
              </div>
            </div>
            <div className="rsp-hero-progress-wrap">
              <div className="rsp-hero-progress-bg" />
              <div className="rsp-hero-progress-fill" style={{ width: `${progressPct}%` }} />
              <div className="rsp-hero-progress-label" style={{ left: `${progressPct}%` }}>{progressPct}%</div>
            </div>
            <h1 className="rsp-hero-title">{raffleTitle}</h1>
            {raffleDescription && (
              <p className="rsp-hero-desc">{raffleDescription}</p>
            )}
          </div>
        </div>
      </section>

      {/* ── PURCHASE SECTION ── */}
      <div id="comprar" className="rsp-purchase-section">
        <div className="rsp-purchase-container">
          <article className="rsp-purchase-card">
            <div className="rsp-tickets-header">
              <h2 className="rsp-tickets-title">TICKETS</h2>
              <div className="rsp-ticket-counter">
                <button className="rsp-counter-btn" onClick={decreaseTickets} disabled={ticketCount <= 1} aria-label="Disminuir">−</button>
                <div className="rsp-counter-display">
                  <span className="rsp-counter-num">{ticketCount}</span>
                  <span className="rsp-counter-label">TICKET</span>
                </div>
                <button className="rsp-counter-btn" onClick={increaseTickets} aria-label="Aumentar">+</button>
              </div>
              <h3 className="rsp-total-price">Total: <span>{totalFormatted}</span></h3>
            </div>

            <h4 className="rsp-section-title"><span className="icon">🏛️</span>DATOS PERSONALES</h4>
            <div className="rsp-form-row">
              <div className="rsp-form-group">
                <label className="rsp-form-label">Nombres y Apellidos *</label>
                <input type="text" className="rsp-form-input" placeholder="Nombres y Apellidos *" value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>
              <div className="rsp-form-group">
                <label className="rsp-form-label">Teléfono *</label>
                <div className="rsp-phone-row">
                  <select className="rsp-phone-select" value={countryCode} onChange={(e) => setCountryCode(e.target.value)}>
                    {COUNTRY_CODES.map((c, i) => (
                      <option key={i} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                  <input type="tel" className="rsp-form-input" placeholder="Teléfono *" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
              </div>
            </div>

            <h4 className="rsp-section-title"><span className="icon">🏦</span>MODOS DE PAGO</h4>
            <p className="rsp-payment-subtitle">Elige una opción.</p>
            <div className="rsp-payment-options">
              <div className={`rsp-payment-option${selectedPayment === 'pagomovil' ? ' active' : ''}`} onClick={() => setSelectedPayment('pagomovil')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setSelectedPayment('pagomovil')}>
                <img src={PAGOMOVIL_LOGO} alt="PagomóvilBVC" />
                {selectedPayment === 'pagomovil' && <div className="rsp-payment-check">✔</div>}
              </div>
              <div className={`rsp-payment-option${selectedPayment === 'binance' ? ' active' : ''}`} onClick={() => setSelectedPayment('binance')} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setSelectedPayment('binance')}>
                <img src={BINANCE_LOGO} alt="BINANCE" />
                {selectedPayment === 'binance' && <div className="rsp-payment-check">✔</div>}
              </div>
            </div>

            {selectedPayment === 'pagomovil' && (
              <div className="rsp-payment-details">
                <p className="rsp-payment-name">{paymentInfo.bank} <span className="badge">✔</span></p>
                <div className="rsp-payment-field-label">{paymentInfo.bank} PAGO MÓVIL</div>
                <div className="rsp-payment-field-value"><h3>{paymentInfo.phone}</h3><CopyButton text={paymentInfo.phone} /></div>
                <div className="rsp-payment-field-label">CÉDULA</div>
                <div className="rsp-payment-field-value"><h3>{paymentInfo.cedula}</h3><CopyButton text={paymentInfo.cedula} /></div>
                <div className="rsp-payment-field-label">{paymentInfo.bank.toUpperCase()}</div>
                <div className="rsp-payment-field-value"><h3>{paymentInfo.bankCode}</h3><CopyButton text={paymentInfo.bankCode} /></div>
                <p className="rsp-payment-titular"><strong>TITULAR</strong><br />{paymentInfo.titular}</p>
                <div className="rsp-payment-total-badge">
                  <strong>Total: {totalFormatted}</strong> <span>({ticketCount} ticket{ticketCount > 1 ? 's' : ''})</span>
                </div>
              </div>
            )}
            {selectedPayment === 'binance' && (
              <div className="rsp-payment-details">
                <p className="rsp-payment-name">No disponible</p>
              </div>
            )}

            <h4 className="rsp-section-title"><span className="icon">📄</span>COMPROBANTE DE PAGO</h4>
            <p className="rsp-payment-subtitle">Foto o Captura de Pantalla</p>

            <div style={{ position: 'relative' }}>
              <div className="rsp-upload-area">
                <div className="rsp-upload-icon">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <span className="rsp-upload-text">{fileName || 'Foto/Captura de Pantalla'}</span>
                <button className="rsp-upload-btn" type="button">Choose File</button>
                <input type="file" accept="image/*" className="rsp-upload-input" onChange={handleFileChange} aria-label="Subir comprobante" />
              </div>
              <p className="rsp-upload-summary">
                <strong>{selectedPayment === 'pagomovil' ? paymentInfo.bank.toUpperCase() : 'No disponible'}: {totalFormatted}</strong>{' '}
                ({ticketCount} ticket{ticketCount > 1 ? 's' : ''})
              </p>
            </div>

            <div className="rsp-terms-row">
              <input type="checkbox" id="terms-dyn" checked={termsAccepted} onChange={(e) => setTermsAccepted(e.target.checked)} />
              <label htmlFor="terms-dyn" className="rsp-terms-label">
                Acepto los <a href="#">Términos y Condiciones</a>
              </label>
            </div>

            {submitSuccess ? (
              <div className="rsp-success-wrap">
                <div className="rsp-success-badge"><span>✅</span> ¡Compra Realizada!</div>
                {purchasedTicket.length > 0 && purchasedTicket.map((t, i) => (
                  <TicketCard key={i} ticket={t} raffleTitle={raffleTitle} />
                ))}
                <a
                  href="https://whatsapp.com/channel/0029VbDKMnT2UPBDxm2mbD2W"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rsp-whatsapp-channel-btn"
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  <span>Únete a nuestro Canal de WhatsApp y sé el primero en enterarte de nuestros Eventos y Ganadores</span>
                </a>
                <button
                  style={{ marginTop: '20px', background: '#948f30', color: '#fff', border: 'none', borderRadius: '100px', padding: '10px 28px', fontFamily: 'Poppins, sans-serif', fontWeight: 700, cursor: 'pointer', fontSize: '14px' }}
                  onClick={() => { setSubmitSuccess(false); setPurchasedTicket([]); }}
                >
                  Hacer otro pedido
                </button>
              </div>
            ) : (
              <>
                {submitError && (
                  <div style={{ background: '#fff3f3', border: '1px solid #f44336', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: '#c62828' }}>
                    {submitError}
                  </div>
                )}
                <button className="rsp-confirm-btn" type="button" onClick={handleConfirm} disabled={isSubmitting} style={{ opacity: isSubmitting ? 0.7 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>
                  {isSubmitting ? 'Registrando...' : 'Confirmar'}
                </button>
              </>
            )}
          </article>

          <p className="rsp-recaptcha-note">
            Sitio Web protegido por reCaptcha™ de Google. Aplican su{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Política de Privacidad</a>
            {' '}y{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Términos del Servicio</a>
          </p>
          <p className="rsp-help-center">Centro de Ayuda: <strong>+58 4144713576</strong></p>
        </div>
      </div>

      {/* ── PAYMENT WARNING MODAL ── */}
      {showPaymentWarning && (
        <div className="rsp-warning-overlay" onClick={() => setShowPaymentWarning(false)}>
          <div className="rsp-warning-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rsp-warning-modal-header">
              <span className="rsp-warning-modal-header-icon">⚠️</span>
              <h2 className="rsp-warning-modal-header-title">¡Atención! Verifica tu Pago</h2>
            </div>
            <div className="rsp-warning-modal-body">
              <p>Antes de confirmar, asegúrate de haber realizado el pago <strong>únicamente</strong> a los siguientes datos de Pago Móvil:</p>
              <div className="rsp-warning-modal-data">
                <p className="rsp-warning-modal-data-title">✅ Datos de Pago Válidos</p>
                <div className="rsp-warning-modal-data-row">
                  <span className="label">Banco</span>
                  <span>{paymentInfo.bank}</span>
                </div>
                <div className="rsp-warning-modal-data-row">
                  <span className="label">Teléfono</span>
                  <span>{paymentInfo.phone}</span>
                </div>
                <div className="rsp-warning-modal-data-row">
                  <span className="label">Cédula</span>
                  <span>{paymentInfo.cedula}</span>
                </div>
              </div>
              <div className="rsp-warning-modal-alert">
                🚫 Si realizaste el pago a otro banco o número diferente, tu ticket <strong>NO será válido</strong>. Debes realizar nuevamente el pago a los datos indicados y subir el comprobante correcto.
              </div>
            </div>
            <div className="rsp-warning-modal-footer">
              <button className="rsp-warning-modal-confirm-btn" onClick={handleConfirmAfterWarning}>
                ✅ Sí, pagué a los datos correctos — Confirmar
              </button>
              <button className="rsp-warning-modal-cancel-btn" onClick={() => setShowPaymentWarning(false)}>
                Cancelar — Revisar mi pago
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── VERIFICADOR ── */}
      <div id="tickets" className="rsp-verifier-section">
        <div className="rsp-verifier-container">
          <h2 className="rsp-verifier-title">VERIFICADOR</h2>
          <p className="rsp-verifier-subtitle">DE TICKETS</p>
          <div className="rsp-verifier-input-wrap">
            <input type="tel" className="rsp-verifier-input" placeholder="Número de Teléfono" value={verifierQuery} onChange={(e) => setVerifierQuery(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleVerify()} />
          </div>
          <button className="rsp-verifier-btn" type="button" onClick={handleVerify} disabled={isVerifying}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            {isVerifying ? 'Buscando...' : 'Buscar'}
          </button>
          {verifierError && (
            <div style={{ marginTop: '16px', background: '#fff3f3', border: '1px solid #f44336', borderRadius: '8px', padding: '10px 14px', fontFamily: 'Poppins, sans-serif', fontSize: '13px', color: '#c62828' }}>
              {verifierError}
            </div>
          )}
          {verifierResults.length > 0 && (
            <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {verifierResults.map((ticket, idx) => (
                <TicketCard key={idx} ticket={ticket} raffleTitle={raffleTitle} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer id="contacto" className="rsp-footer">
        <div className="rsp-footer-container">
          <div className="rsp-footer-col">
            <p className="rsp-footer-brand-name">RIFAS CAMIGANGA</p>
            <p className="rsp-footer-brand-sub">RIFAS CAMIGANGA</p>
          </div>
          <div className="rsp-footer-col">
            <h3 className="rsp-footer-col-title">Secciones</h3>
            <ul className="rsp-footer-links">
              <li><a href="#top">Inicio</a></li>
              <li><a href="#comprar">Lista de Tickets</a></li>
              <li><a href="#tickets">Verificar mis Tickets</a></li>
            </ul>
          </div>
          <div className="rsp-footer-col">
            <h3 className="rsp-footer-col-title">Contacto</h3>
            <ul className="rsp-footer-contact-list">
              <li><span>🌐</span><a href="https://www.rifascamiganga.com" target="_blank" rel="noopener noreferrer">www.rifascamiganga.com</a></li>
              <li><span>📍</span><span>Valencia, Carabobo, Venezuela</span></li>
              <li><span>📞</span><span>0414-4393801</span></li>
            </ul>
          </div>
          <div className="rsp-footer-col">
            <h3 className="rsp-footer-col-title">Síguenos</h3>
            <div className="rsp-footer-social">
              <a href="https://www.instagram.com/camiganga10" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              </a>
              <a href="https://www.tiktok.com/@camigana" target="_blank" rel="noopener noreferrer" aria-label="TikTok">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor"><path d="M9 0h1.98c.144.715.54 1.617 1.235 2.512C12.895 3.389 13.797 4 15 4v2c-1.753 0-3.07-.814-4-1.829V11a5 5 0 1 1-5-5v2a3 3 0 1 0 3 3V0Z" /></svg>
              </a>
            </div>
          </div>
        </div>
        <div className="rsp-footer-credits">
          <a href="https://www.rifarito.com" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', color: 'rgba(255,255,255,0.7)' }}>
            <span style={{ fontSize: '12px', fontFamily: 'Poppins, sans-serif' }}>WEB PROGRAMADA POR</span>
            <img src={RIFARITO_WATERMARK} alt="Sistema de Rifas" style={{ height: '28px', width: 'auto' }} />
          </a>
          <p>
            <a href="/legal/terms.html" target="_blank" rel="noopener noreferrer">Condiciones</a>
            {' | '}
            <a href="/legal/privacy.html" target="_blank" rel="noopener noreferrer">Privacidad</a>
            {' | '}
            <a href="/legal/disclaimer.html" target="_blank" rel="noopener noreferrer">Responsabilidad</a>
            <br />© 2025 Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* ── FLOATING WHATSAPP CHANNEL ── */}
      <a
        href="https://www.whatsapp.com/channel/0029VbDKMnT2UPBDxm2mbD2W"
        target="_blank"
        rel="noopener noreferrer"
        className="rsp-wa-float-btn"
        aria-label="Únete al canal de WhatsApp"
      >
        <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        <span className="rsp-wa-float-btn-label">Únete aquí</span>
      </a>

      {/* ── FLOATING CHAT ── */}
      <div className="rsp-chat-btn" onClick={() => setChatOpen(!chatOpen)} role="button" tabIndex={0} aria-label="Chat" onKeyDown={(e) => e.key === 'Enter' && setChatOpen(!chatOpen)}>
        <span className="rsp-chat-btn-icon">💬</span>
        <span className="rsp-chat-btn-label">Chat</span>
      </div>
      <div className={`rsp-chat-links${chatOpen ? ' active' : ''}`}>
        <a href="https://wa.me/+5804144713576" rel="noopener noreferrer" target="_blank" className="whatsapp" aria-label="WhatsApp">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
        </a>
        <a href="https://t.me/camilortizexchange" rel="noopener noreferrer" target="_blank" className="telegram" aria-label="Telegram">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
        </a>
      </div>
    </>
  );
}